import { UserRoundIcon } from 'lucide-react';
import { HeadingText } from '../shared/HeadingText';
import { Reveal } from '../shared/Reveal';
import { usePages, useSite } from '../../contexts/SiteContext';
import { useCatalog } from '../../contexts/CatalogContext';
import { useOverlayActions } from '../../contexts/StoreContext';
import { teamOnSite } from '../../core/rules/salonDays';
import { fill } from '../../utils/copy';
import { quietLink, sectionHeading } from '../../ui/sections';

/**
 * "Ook in de salon": de collega's naast Sarah, op de salonpagina, want daar kies je bij wie je
 * komt. Alleen wie Sarah in Instellingen › Team op "Toon op de salonpagina" zet; zonder zulke
 * collega's staat het blok er niet (de hero noemt Sarah al).
 *
 * Per collega: portret, naam, één zin, de alinea en welke behandelingen zij doet. Eén collega:
 * portret en tekst naast elkaar over de volle breedte; meer: twee kolommen.
 */
export function SalonTeam() {
  const pages = usePages();
  const site = useSite();
  const {
    treatments
  } = useCatalog();
  const {
    openBooking
  } = useOverlayActions();
  const copy = pages.treatments.team;
  const colleagues = teamOnSite(site.staff);
  if (colleagues.length === 0) return null;
  const doesFor = (allTreatments: boolean, slugs: string[]) => allTreatments ? ['Alle behandelingen'] : treatments.filter(t => slugs.includes(t.slug)).map(t => t.name);
  const single = colleagues.length === 1;
  return <section className="bg-sand py-20 sm:py-24 lg:py-32" aria-labelledby="salon-team" data-id="element-8487" data-mp="2js0t9slnsln7">
      <div className="mx-auto max-w-content px-6 lg:px-10" data-id="element-8488" data-mp="2op9g46hr5dus">
        <div className="max-w-2xl" data-id="element-8489" data-mp="2onpv62l5iemo">
          <h2 id="salon-team" className={`${sectionHeading} text-ink`} data-id="element-8490" data-mp="2tovq872aadzd">
            <HeadingText heading={copy.heading} data-id="element-8491" data-mp="2yfvr1neixg75" />
          </h2>
          {copy.body && <p className="mt-5 text-[17px] leading-relaxed text-muted" data-id="element-8492" data-mp="2yhyogsi0ablw">{fill(copy.body)}</p>}
        </div>

        <ul className={`mt-12 grid gap-12 lg:mt-14 ${single ? '' : 'md:grid-cols-2 md:gap-10'}`} data-id="element-8493" data-mp="20ynhf5tn15xg">
          {colleagues.map((s, i) => {
          const does = doesFor(s.allTreatments, s.treatments);
          return <Reveal as="li" key={s.id} delay={Math.min(i, 3) * 0.05} data-id="element-8494" data-mp="1t0bt37btpua6" data-mpi={i}>
                <article aria-label={s.name} className={`grid gap-6 ${single ? 'sm:grid-cols-[minmax(0,2fr)_minmax(0,3fr)] sm:gap-10 lg:grid-cols-[minmax(0,5fr)_minmax(0,7fr)] lg:gap-16' : 'grid-cols-[7rem_minmax(0,1fr)] sm:grid-cols-[9rem_minmax(0,1fr)]'}`} data-id="element-8495" data-mp="1sxxyezl22k35">
                  {s.photo ? <img src={s.photo} alt={`Portret van ${s.name}`} loading="lazy" className="aspect-[4/5] w-full object-cover" data-id="element-8496" data-mp="1a1zjjkh02ie2" /> : <span className="flex aspect-[4/5] w-full items-center justify-center bg-shell text-muted" aria-hidden="true" data-id="element-8497" data-mp="1ovud7j8l414o">
                      <UserRoundIcon className="h-8 w-8" strokeWidth={1.25} data-id="element-8498" data-mp="30ckm5a4e771z" />
                    </span>}
                  <div className={`min-w-0 ${single ? 'sm:self-center' : ''}`} data-id="element-8499" data-mp="2vdhgbngl8wsy">
                    <h3 className={`font-display font-light leading-tight text-ink ${single ? 'text-[2rem] sm:text-[2.4rem]' : 'text-[1.6rem]'}`} data-id="element-8500" data-mp="32xiiykv7yh54">{s.name}</h3>
                    {s.intro && <p className="mt-1.5 text-[15px] text-ink" data-id="element-8501" data-mp="2tx3pgy83pvqj">{s.intro}</p>}
                    {s.bio && <p className="mt-4 max-w-xl whitespace-pre-line text-[16px] leading-relaxed text-muted" data-id="element-8502" data-mp="2tzge1u8d74ck">{s.bio}</p>}
                    {does.length > 0 && <ul aria-label={`Behandelingen van ${s.name}`} className="mt-5 flex flex-wrap gap-2" data-id="element-8503" data-mp="3lotan48rqxy2">
                        {does.map(name => <li key={name} className="rounded-full border border-ink/15 px-3 py-1 text-[13px] text-ink" data-id="element-8504" data-mp="3cwhkljhkhcim">
                            {name}
                          </li>)}
                      </ul>}
                    <button type="button" onClick={() => openBooking()} aria-haspopup="dialog" className={`mt-5 ${quietLink} text-ink underline decoration-ink/25 hover:decoration-ink`} data-id="element-8505" data-mp="8ggc9g8pw56e">
                      Plan een afspraak bij {s.name}
                    </button>
                  </div>
                </article>
              </Reveal>;
        })}
        </ul>
      </div>
    </section>;
}